import { useMemo, useState } from 'react'
import { useParams, useSearchParams, useNavigate } from 'react-router-dom'
import { toast } from 'react-hot-toast'
import {
  Plus,
  Layers,
  Kanban,
  AlertTriangle,
  User,
  ChevronRight,
  Check,
  FileText,
  ChevronDown,
} from 'lucide-react'
import { useIssues, useStatuses, useIssueMutations, useSpace, useUpdateTimeTracking } from '../../../features/spaces/hooks'
import { useAuth } from '../../../context/AuthContext'
import { IssueModal } from '../../../features/spaces/components/IssueModal'
import { NewIssueModal } from '../../../features/spaces/components/NewIssueModal'
import { getIssueErrorMessage } from '../../../features/spaces/api'
import type { Issue, WorkflowStatus } from '../../../features/spaces/api'
import { useUserOptions, UserOption } from '../../../shared/hooks/useUserOptions'
import { IssueActionsMenu } from '../../../features/spaces/components/IssueActionsMenu'
import { doesIssueMatchStatus } from '../boardUtils'
import { IssueTypeIcon } from '../../../features/spaces/components/ListRow'
import { isSuperAdminOrOwner } from '@jira-clone/shared-types'

function SubtaskBranchIcon({ className = 'h-3.5 w-3.5' }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="2" y="2" width="4.5" height="4.5" rx="1" />
      <rect x="9.5" y="9.5" width="4.5" height="4.5" rx="1" />
      <path d="M4.25 6.5v3.5a2 2 0 0 0 2 2h3.25" />
    </svg>
  )
}

function formatBoardDueDate(dateStr?: string | null): string {
  if (!dateStr) return ''
  try {
    const d = new Date(dateStr)
    if (isNaN(d.getTime())) return ''
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  } catch {
    return ''
  }
}

function isDateOverdue(dateStr?: string | null, statusCategory?: string): boolean {
  if (!dateStr || statusCategory === 'done') return false
  const due = new Date(dateStr).getTime()
  if (isNaN(due)) return false
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return due < today.getTime()
}

function IssueCard({
  issue,
  subtasks = [],
  subtaskDoneCount = 0,
  members = [],
  onClick,
  onOpenIssue,
  onDragStart,
  onMenuEdit,
  onMenuDelete,
  onMenuMove,
  onMenuLogTime,
  onToggleSubtask,
  onAddSubtask,
  allStatuses,
}: {
  issue: Issue
  subtasks?: Issue[]
  subtaskDoneCount?: number
  members?: UserOption[]
  onClick: () => void
  onOpenIssue: (id: string) => void
  onDragStart: (e: React.DragEvent) => void
  onMenuEdit: () => void
  onMenuDelete: () => void
  onMenuMove: (statusId: string) => void
  onMenuLogTime?: () => void
  onToggleSubtask: (subtask: Issue, e: React.MouseEvent) => void
  onAddSubtask: (parentId: string, title: string) => Promise<void>
  allStatuses: Array<{ id: string; name: string }>
}) {
  const [isSubtasksExpanded, setIsSubtasksExpanded] = useState(false)
  const [isAddingSubtask, setIsAddingSubtask] = useState(false)
  const [newSubtaskTitle, setNewSubtaskTitle] = useState('')

  const assignee = useMemo(() => {
    if (!issue.assignee_id) return null

    // Search in userOptions (real users, via /users/assignable)
    const foundMember = members.find((m) => m.id === issue.assignee_id)
    if (foundMember) {
      const name = foundMember.name || foundMember.display_name?.split(' — ')[0] || foundMember.display_name
      const initials =
        foundMember.initials ||
        name
          .split(' ')
          .map((n) => n[0])
          .join('')
          .slice(0, 2)
          .toUpperCase()
      return {
        id: foundMember.id,
        name,
        initials,
        role: foundMember.role || '',
        color: foundMember.color || '#2563eb',
      }
    }

    // Fallback: string ID (e.g. a user no longer active/assignable)
    const fallbackName = typeof issue.assignee_id === 'string' ? issue.assignee_id : 'User'
    return {
      id: issue.assignee_id,
      name: fallbackName,
      initials: fallbackName.slice(0, 2).toUpperCase(),
      role: 'Member',
      color: '#6366f1',
    }
  }, [issue.assignee_id, members])

  const formattedDueDate = useMemo(() => formatBoardDueDate(issue.due_date), [issue.due_date])
  const isOverdue = useMemo(
    () => isDateOverdue(issue.due_date, issue.status_category),
    [issue.due_date, issue.status_category]
  )

  return (
    <div
      draggable
      onDragStart={onDragStart}
      onClick={onClick}
      className="group rounded-xl border border-[#DFE1E6] bg-white shadow-2xs hover:shadow-md hover:border-[#4C9AFF] transition-all cursor-pointer dark:border-slate-800 dark:bg-slate-900 active:scale-[0.99] select-none overflow-hidden"
    >
      <div className="p-3.5">
        {/* Title & Hover Actions Menu */}
        <div className="flex items-start justify-between gap-2">
          <h4 className="text-sm font-medium text-[#172B4D] dark:text-slate-100 leading-snug line-clamp-2">
            {issue.title}
          </h4>
          <div
            onClick={(e) => e.stopPropagation()}
            className="opacity-0 group-hover:opacity-100 transition-opacity shrink-0 -mr-1 -mt-1"
          >
            <IssueActionsMenu
              onEdit={onMenuEdit}
              onDelete={onMenuDelete}
              
              
              onLogTime={onMenuLogTime}
            />
          </div>
        </div>

        {/* Due Date Row (matches: Jul 29, 2026 ⚠) */}
        {formattedDueDate && (
          <div className="mt-2 flex items-center gap-1.5 text-xs text-[#5E6C84] dark:text-slate-400 font-normal">
            <span>{formattedDueDate}</span>
            {isOverdue && (
              <span title="Overdue" className="inline-flex items-center text-[#DE350B]">
                <AlertTriangle className="h-3.5 w-3.5 stroke-[2] shrink-0 text-[#DE350B]" />
              </span>
            )}
          </div>
        )}

        {/* Bottom Row: Type Icon + Key on left, Assignee Avatar on right */}
        <div className="mt-3.5 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <IssueTypeIcon type={issue.type} className="h-4 w-4" />
            <span className="text-xs font-semibold text-[#5E6C84] dark:text-slate-400 truncate">
              {issue.key}
            </span>
            {issue.story_points ? (
              <span className="rounded bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 text-[10px] font-bold text-slate-600 dark:text-slate-300">
                {issue.story_points}
              </span>
            ) : null}
          </div>

          {/* Assignee Avatar */}
          {assignee ? (
            <div
              className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-white text-[10px] font-bold shadow-2xs border border-white dark:border-slate-800"
              style={{ backgroundColor: assignee.color }}
              title={`${assignee.name} (${assignee.role})`}
            >
              {assignee.initials}
            </div>
          ) : (
            <div
              className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#EBECF0] dark:bg-slate-800 text-[#6B778C] dark:text-slate-400 border border-[#DFE1E6] dark:border-slate-700"
              title="Unassigned"
            >
              <User className="h-3.5 w-3.5 stroke-[2]" />
            </div>
          )}
        </div>
      </div>

      {/* Subtasks Footer Bar (matches: 🔀 Subtasks 0/1 >) */}
      {subtasks.length > 0 && (
        <div className="border-t border-[#EBECF0] dark:border-slate-800 bg-[#FAFBFC] dark:bg-slate-900/40">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              setIsSubtasksExpanded((v) => !v)
            }}
            className="w-full flex items-center justify-between px-3.5 py-2.5 text-xs font-medium text-[#172B4D] dark:text-slate-200 hover:bg-[#F4F5F7] dark:hover:bg-slate-800/60 transition-colors cursor-pointer"
          >
            <div className="flex items-center gap-2">
              <SubtaskBranchIcon className="h-3.5 w-3.5 text-[#5E6C84] shrink-0" />
              <span className="font-medium text-[#172B4D] dark:text-slate-200">Subtasks</span>
              <span className="rounded px-1.5 py-0.5 text-[11px] font-semibold bg-[#EBECF0] text-[#42526E] dark:bg-slate-800 dark:text-slate-300">
                {subtaskDoneCount}/{subtasks.length}
              </span>
            </div>
            <ChevronRight
              className={`h-3.5 w-3.5 text-[#5E6C84] transition-transform duration-200 ${
                isSubtasksExpanded ? 'rotate-90' : ''
              }`}
            />
          </button>

          {/* Subtasks Inline Expanded List */}
          {isSubtasksExpanded && (
            <div className="px-3 pb-2.5 pt-0.5 space-y-1.5 border-t border-[#EBECF0]/60 dark:border-slate-800/60">
              {subtasks.map((st) => {
                const isDone = st.status_category === 'done' || st.status === 'done'
                return (
                  <div
                    key={st.id}
                    onClick={(e) => {
                      e.stopPropagation()
                      onOpenIssue(st.id)
                    }}
                    className="group/st flex items-center justify-between gap-2 p-1.5 rounded-lg hover:bg-white dark:hover:bg-slate-800 border border-transparent hover:border-slate-200 dark:hover:border-slate-700 transition-all cursor-pointer"
                  >
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <button
                        type="button"
                        onClick={(e) => onToggleSubtask(st, e)}
                        className={`h-4 w-4 rounded flex items-center justify-center shrink-0 border transition-colors cursor-pointer ${
                          isDone
                            ? 'bg-emerald-500 border-emerald-500 text-white'
                            : 'border-slate-300 dark:border-slate-600 hover:border-blue-500 bg-white dark:bg-slate-900'
                        }`}
                        title={isDone ? 'Mark subtask incomplete' : 'Mark subtask complete'}
                      >
                        {isDone && <Check className="h-3 w-3 stroke-[3]" />}
                      </button>
                      <span className="font-mono text-[10px] font-bold text-slate-400 dark:text-slate-500 shrink-0">
                        {st.key}
                      </span>
                      <span
                        className={`text-xs truncate ${
                          isDone
                            ? 'line-through text-slate-400 dark:text-slate-500'
                            : 'text-slate-700 dark:text-slate-200 font-medium'
                        }`}
                      >
                        {st.title}
                      </span>
                    </div>
                  </div>
                )
              })}

              {/* Inline Add Subtask */}
              {isAddingSubtask ? (
                <div
                  className="flex items-center gap-1.5 pt-1"
                  onClick={(e) => e.stopPropagation()}
                >
                  <input
                    autoFocus
                    className="flex-1 rounded border border-blue-400 bg-white px-2 py-1 text-xs outline-none focus:ring-1 focus:ring-blue-500 dark:bg-slate-800 dark:text-slate-100 dark:border-blue-500"
                    placeholder="Subtask title..."
                    value={newSubtaskTitle}
                    onChange={(e) => setNewSubtaskTitle(e.target.value)}
                    onKeyDown={async (e) => {
                      if (e.key === 'Enter' && newSubtaskTitle.trim()) {
                        await onAddSubtask(issue.id, newSubtaskTitle.trim())
                        setNewSubtaskTitle('')
                        setIsAddingSubtask(false)
                      }
                      if (e.key === 'Escape') {
                        setIsAddingSubtask(false)
                        setNewSubtaskTitle('')
                      }
                    }}
                  />
                  <button
                    type="button"
                    onClick={async () => {
                      if (newSubtaskTitle.trim()) {
                        await onAddSubtask(issue.id, newSubtaskTitle.trim())
                        setNewSubtaskTitle('')
                        setIsAddingSubtask(false)
                      }
                    }}
                    className="rounded bg-blue-600 px-2 py-1 text-[11px] font-semibold text-white hover:bg-blue-700 cursor-pointer"
                  >
                    Add
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setIsAddingSubtask(false)
                      setNewSubtaskTitle('')
                    }}
                    className="text-xs text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 px-1 cursor-pointer"
                  >
                    ✕
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    setIsAddingSubtask(true)
                  }}
                  className="flex items-center gap-1 text-[11px] font-medium text-slate-500 hover:text-blue-600 dark:text-slate-400 dark:hover:text-blue-400 pt-1 transition-colors cursor-pointer"
                >
                  <Plus className="h-3 w-3" />
                  <span>Add subtask</span>
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function Column({
  status,
  issues,
  subtasksByParent,
  subtaskDoneCount,
  members = [],
  onAddIssue,
  onOpenIssue,
  onDropIssue,
  onMenuEdit,
  onMenuDelete,
  onMenuMove,
  onMenuLogTime,
  onToggleSubtask,
  onAddSubtask,
  allStatuses,
}: {
  status: WorkflowStatus
  issues: Issue[]
  subtasksByParent: Map<string, Issue[]>
  subtaskDoneCount: Map<string, number>
  members?: UserOption[]
  onAddIssue: () => void
  onOpenIssue: (id: string) => void
  onDropIssue: (issueId: string, targetStatus: WorkflowStatus) => void
  onMenuEdit: (issueId: string) => void
  onMenuDelete: (issueId: string) => void
  onMenuMove: (issueId: string, statusId: string) => void
  onMenuLogTime?: (issueId: string) => void
  onToggleSubtask: (subtask: Issue, e: React.MouseEvent) => void
  onAddSubtask: (parentId: string, title: string) => Promise<void>
  allStatuses: Array<{ id: string; name: string }>
}) {
  const [isOver, setIsOver] = useState(false)

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    if (!isOver) setIsOver(true)
  }

  const handleDragLeave = () => {
    setIsOver(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsOver(false)
    const issueId = e.dataTransfer.getData('text/plain')
    if (issueId) {
      onDropIssue(issueId, status)
    }
  }

  const statusColor =
    status.color ||
    (status.category === 'done'
      ? '#10b981'
      : status.category === 'in_progress'
      ? '#3b82f6'
      : '#94a3b8')

  return (
    <div
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={`flex h-full w-full flex-col rounded-2xl border transition-all duration-150 ${
        isOver
          ? 'border-blue-500 ring-2 ring-blue-500/20 bg-blue-50/50 dark:border-blue-500 dark:bg-blue-950/20'
          : 'border-slate-200/90 bg-slate-100/70 dark:border-slate-800/80 dark:bg-slate-900/50 shadow-xs'
      }`}
    >
      {/* Column Header */}
      <div className="flex items-center justify-between px-3.5 py-3 border-b border-slate-200/70 dark:border-slate-800/70 bg-white/70 dark:bg-slate-900/70 rounded-t-2xl backdrop-blur-xs">
        <div className="flex items-center gap-2">
          <span
            className="h-2.5 w-2.5 rounded-full shrink-0 shadow-xs"
            style={{ backgroundColor: statusColor }}
          />
          <h3 className="text-xs font-bold uppercase tracking-wider text-slate-800 dark:text-slate-200">
            {status.name}
          </h3>
          <span className="flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-slate-200/80 dark:bg-slate-800 text-[11px] font-bold text-slate-600 dark:text-slate-300">
            {issues.length}
          </span>
        </div>
        <button
          onClick={onAddIssue}
          className="flex h-6 w-6 items-center justify-center rounded-lg text-slate-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:text-blue-400 dark:hover:bg-slate-800 transition-colors cursor-pointer"
          title={`Add issue to ${status.name}`}
        >
          <Plus className="h-3.5 w-3.5 stroke-[2.5]" />
        </button>
      </div>

      {/* Column Cards */}
      <div className="flex-1 space-y-2.5 overflow-y-auto p-2.5 scrollbar-thin">
        {issues.map((issue) => (
          <IssueCard
            key={issue.id}
            issue={issue}
            subtasks={subtasksByParent.get(issue.id) ?? []}
            subtaskDoneCount={subtaskDoneCount.get(issue.id) ?? 0}
            members={members}
            onClick={() => onOpenIssue(issue.id)}
            onOpenIssue={onOpenIssue}
            onDragStart={(e) => {
              e.dataTransfer.setData('text/plain', issue.id)
            }}
            onMenuEdit={() => onMenuEdit(issue.id)}
            onMenuDelete={() => onMenuDelete(issue.id)}
            onMenuMove={(statusId) => onMenuMove(issue.id, statusId)}
            onMenuLogTime={onMenuLogTime ? () => onMenuLogTime(issue.id) : undefined}
            onToggleSubtask={onToggleSubtask}
            onAddSubtask={onAddSubtask}
            allStatuses={allStatuses}
          />
        ))}

        {issues.length === 0 && (
          <div className="flex flex-col items-center justify-center py-14 text-center border-2 border-dashed border-slate-200/80 dark:border-slate-800/80 rounded-xl my-2 mx-1">
            <Layers className="h-6 w-6 text-slate-300 dark:text-slate-600 mb-1.5" />
            <p className="text-xs font-medium text-slate-400 dark:text-slate-500">No issues</p>
            <p className="text-[10px] text-slate-400/80 dark:text-slate-500/80 mt-0.5">Drag cards here or click +</p>
          </div>
        )}
      </div>

      {/* Column Footer: Quick add issue */}
      <div className="p-2 border-t border-slate-200/50 dark:border-slate-800/50 bg-white/40 dark:bg-slate-900/40 rounded-b-2xl">
        <button
          type="button"
          onClick={onAddIssue}
          className="w-full flex items-center justify-center gap-1.5 py-1.5 text-xs font-semibold text-slate-500 hover:text-blue-600 hover:bg-white dark:text-slate-400 dark:hover:text-blue-400 dark:hover:bg-slate-800 rounded-lg transition-colors cursor-pointer"
        >
          <Plus className="h-3.5 w-3.5" />
          <span>Add issue</span>
        </button>
      </div>
    </div>
  )
}

export function BoardPage() {
  const { id: spaceId } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const selectedIssueId = searchParams.get('selectedIssue')

  const { user } = useAuth()
  const updateTimeTracking = useUpdateTimeTracking(spaceId ?? null)

  // Check if current user is superadmin / owner
  const isSuperAdmin = Boolean(
    (user?.role && isSuperAdminOrOwner(user.role)) ||
    user?.department?.toLowerCase() === 'superadmin'
  )

  // Always fetch only current user's tasks (onlyMine: true), unless superadmin
  const { data: space } = useSpace(spaceId ?? null)
  const { data: issues = [], isLoading } = useIssues(spaceId ?? null, { onlyMine: !isSuperAdmin })
  const { data: statuses = [] } = useStatuses(spaceId ?? null)
  const { data: userOptions = [] } = useUserOptions(spaceId ?? null)
  const { create, update, remove } = useIssueMutations(spaceId ?? null)

  const [creatingStatus, setCreatingStatus] = useState<string | null>(null)
  const [newTitle, setNewTitle] = useState('')
  const [newIssueModalOpen, setNewIssueModalOpen] = useState(false)
  const [showAddMenu, setShowAddMenu] = useState(false)

  // Top-level issues (only top-level tasks appear as standalone cards on board columns, subtasks nest within parent cards)
  const parentIds = useMemo(() => new Set((issues as Issue[]).map((i) => i.id)), [issues])

  // Strictly filter to current user's own tasks (matching ListPage behavior)
  // Superadmins see all tasks; regular users see only their assigned tasks
  const filteredIssues = useMemo(() => {
    if (isSuperAdmin) return issues as Issue[]
    if (!user?.id) return issues as Issue[]

    const currentUserId = String(user.id)
    const currentUsername = user.username ? String(user.username) : ''
    const list = issues as Issue[]

    return list.filter((issue: any) => {
      const singleAssignee = issue.assignee_id ? String(issue.assignee_id) : (issue.assignee ? String(typeof issue.assignee === 'object' ? issue.assignee._id || issue.assignee.id : issue.assignee) : '')
      const assignees = Array.isArray(issue.assigneeIds)
        ? issue.assigneeIds.map((a: any) => (typeof a === 'object' ? String(a._id || a.id) : String(a)))
        : []

      const isAssigned = (
        singleAssignee === currentUserId ||
        (currentUsername && singleAssignee === currentUsername) ||
        assignees.includes(currentUserId) ||
        (currentUsername && assignees.includes(currentUsername))
      )
      if (isAssigned) return true

      // Also retain parent task if any of its subtasks are assigned to the user
      const hasAssignedChild = list.some((child: any) => {
        const pId = child.parent_id || child.parentId
        if (pId !== issue.id) return false
        const childAssignee = child.assignee_id ? String(child.assignee_id) : (child.assignee ? String(typeof child.assignee === 'object' ? child.assignee._id || child.assignee.id : child.assignee) : '')
        const childAssignees = Array.isArray(child.assigneeIds)
          ? child.assigneeIds.map((a: any) => (typeof a === 'object' ? String(a._id || a.id) : String(a)))
          : []
        return (
          childAssignee === currentUserId ||
          (currentUsername && childAssignee === currentUsername) ||
          childAssignees.includes(currentUserId) ||
          (currentUsername && childAssignees.includes(currentUsername))
        )
      })

      return hasAssignedChild
    })
  }, [issues, isSuperAdmin, user?.id, user?.username])

  const topLevelIssues = useMemo(() => {
    return filteredIssues.filter((i) => !i.parent_id || !parentIds.has(i.parent_id))
  }, [filteredIssues, parentIds])

  // Subtasks by parent
  const subtasksByParent = useMemo(() => {
    const map = new Map<string, Issue[]>()
    for (const issue of issues as Issue[]) {
      if (!issue.parent_id) continue
      const list = map.get(issue.parent_id) ?? []
      list.push(issue)
      map.set(issue.parent_id, list)
    }
    return map
  }, [issues])

  // Subtask done counts per parent
  const subtaskDoneCount = useMemo(() => {
    const map = new Map<string, number>()
    for (const issue of issues as Issue[]) {
      if (!issue.parent_id) continue
      if (issue.status_category === 'done' || issue.status === 'done') {
        map.set(issue.parent_id, (map.get(issue.parent_id) ?? 0) + 1)
      }
    }
    return map
  }, [issues])

  const selectedIssue = useMemo(() => {
    if (!selectedIssueId) return null
    return (issues as Issue[]).find((i) => i.id === selectedIssueId || i.key === selectedIssueId) ?? null
  }, [issues, selectedIssueId])

  const handleOpenIssue = (issueId: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.set('selectedIssue', issueId)
      return next
    })
  }

  const handleCloseIssueModal = () => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.delete('selectedIssue')
      return next
    })
  }

  const handleAddInline = async (statusId: string) => {
    if (!newTitle.trim()) return
    try {
      await create.mutateAsync({ title: newTitle.trim(), status: statusId })
      setNewTitle('')
      setCreatingStatus(null)
    } catch (err) {
      toast.error(getIssueErrorMessage(err, 'create tasks'))
    }
  }

  const handleDropIssue = async (issueId: string, targetStatus: WorkflowStatus) => {
    const existing = (issues as Issue[]).find((i) => i.id === issueId)
    if (!existing || existing.status === targetStatus.id) return

    try {
      await update.mutateAsync({
        id: issueId,
        payload: {
          status: targetStatus.id,
          status_category: targetStatus.category,
        },
      })
    } catch (err) {
      toast.error(getIssueErrorMessage(err, 'move this task'))
    }
  }

  const handleMenuEdit = (issueId: string) => {
    handleOpenIssue(issueId)
  }

  const handleMenuDelete = async (issueId: string) => {
    try {
      await remove.mutateAsync(issueId)
      toast.success('Issue deleted')
    } catch (err) {
      toast.error(getIssueErrorMessage(err, 'delete this issue'))
    }
  }

  const handleMenuMove = async (issueId: string, targetStatusId: string) => {
    try {
      await update.mutateAsync({
        id: issueId,
        payload: { status: targetStatusId },
      })
      toast.success('Issue moved')
    } catch (err) {
      toast.error(getIssueErrorMessage(err, 'move this issue'))
    }
  }

  const handleLogTime = (issueId: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.set('selectedIssue', issueId)
      return next
    })
  }

  const handleToggleSubtask = async (subtask: Issue, e: React.MouseEvent) => {
    e.stopPropagation()
    const isDone = subtask.status_category === 'done' || subtask.status === 'done'
    const doneStatus = statuses.find((s) => s.category === 'done' || s.id === 'done')
    const todoStatus =
      statuses.find((s) => s.category === 'todo' || s.id === 'todo' || s.id === 'backlog') ||
      statuses[0]
    const targetStatus = isDone ? todoStatus : doneStatus
    if (!targetStatus) return

    try {
      await update.mutateAsync({
        id: subtask.id,
        payload: {
          status: targetStatus.id,
          status_category: targetStatus.category,
        },
      })
      toast.success(isDone ? 'Subtask marked incomplete' : 'Subtask marked complete')
    } catch (err) {
      toast.error(getIssueErrorMessage(err, 'update subtask'))
    }
  }

  const handleAddSubtask = async (parentId: string, title: string) => {
    if (!title.trim() || !spaceId) return
    try {
      await create.mutateAsync({
        title: title.trim(),
        type: 'subtask',
        parent_id: parentId,
        status: statuses[0]?.id || 'todo',
      })
      toast.success('Subtask created')
    } catch (err) {
      toast.error(getIssueErrorMessage(err, 'create subtask'))
    }
  }

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent" />
      </div>
    )
  }

  const sortedStatuses = [...statuses].sort((a, b) => a.position - b.position)

  return (
    <div className="flex h-full w-full flex-col bg-white dark:bg-slate-950 font-sans overflow-hidden">
      {/* Board Subheader */}
      <div className="flex items-center justify-between border-b border-slate-200/80 bg-white px-6 py-3 dark:border-slate-800 dark:bg-slate-900 shrink-0">
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-500/10 text-blue-600 dark:bg-blue-950/60 dark:text-blue-400">
            <Kanban className="h-4 w-4" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-bold text-slate-900 dark:text-slate-100">
                Board
              </h2>
              <span className="rounded-full bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300 text-xs px-2 py-0.5 font-bold border border-blue-200/60 dark:border-blue-900/60">
                {topLevelIssues.length} {topLevelIssues.length === 1 ? 'issue' : 'issues'}
              </span>
            </div>
            <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
              Drag cards between status columns to update issue progression
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <div className="relative">
            <button
              onClick={() => setShowAddMenu((prev) => !prev)}
              className="flex items-center gap-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 px-3.5 py-1.5 text-xs font-semibold text-white shadow-xs transition-colors cursor-pointer"
            >
              <Plus className="h-3.5 w-3.5" /> Create Issue
              <ChevronDown className="h-3 w-3 ml-0.5" />
            </button>
            {showAddMenu && (
              <>
                <div className="fixed inset-0 z-[55]" onClick={() => setShowAddMenu(false)} />
                <div className="absolute right-0 top-full mt-1 w-44 rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-slate-800 dark:bg-slate-900 z-[56] animate-in fade-in zoom-in-95 duration-100">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddMenu(false)
                      setNewIssueModalOpen(true)
                    }}
                    className="flex w-full items-center gap-2.5 px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 dark:text-slate-200 dark:hover:bg-slate-800 transition-colors"
                  >
                    <Plus className="h-3.5 w-3.5" />
                    <span>Create Issue</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddMenu(false)
                      navigate(`/spaces/${spaceId}/docs`)
                    }}
                    className="flex w-full items-center gap-2.5 px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 dark:text-slate-200 dark:hover:bg-slate-800 transition-colors"
                  >
                    <FileText className="h-3.5 w-3.5" />
                    <span>Add document</span>
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Board Columns Container - Expands across full width */}
      <div className="flex-1 min-h-0 overflow-x-auto p-6 scrollbar-thin bg-slate-100/50 dark:bg-slate-950/40">
        <div className="flex gap-4 h-full min-w-full items-stretch">
          {sortedStatuses.map((status) => (
            <div key={status.id} className="flex-1 min-w-[260px] flex flex-col gap-2 h-full">
              <Column
                status={status}
                issues={topLevelIssues.filter((i) => doesIssueMatchStatus(i, status, statuses))}
                subtasksByParent={subtasksByParent}
                subtaskDoneCount={subtaskDoneCount}
                members={userOptions}
                onAddIssue={() => {
                  setCreatingStatus(status.id)
                  setNewTitle('')
                }}
                onOpenIssue={handleOpenIssue}
                onDropIssue={handleDropIssue}
                onMenuEdit={handleMenuEdit}
                onMenuDelete={handleMenuDelete}
                onMenuMove={handleMenuMove}
                onMenuLogTime={handleLogTime}
                onToggleSubtask={handleToggleSubtask}
                onAddSubtask={handleAddSubtask}
                allStatuses={statuses}
              />

              {creatingStatus === status.id && (
                <div className="rounded-xl border border-blue-400 bg-white p-3 shadow-lg dark:border-blue-500 dark:bg-slate-900 animate-in fade-in">
                  <input
                    autoFocus
                    className="w-full rounded-lg border border-slate-200 px-3 py-1.5 text-xs outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                    placeholder="What needs to be done?..."
                    value={newTitle}
                    onChange={(e) => setNewTitle(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleAddInline(status.id)
                      if (e.key === 'Escape') setCreatingStatus(null)
                    }}
                  />
                  <div className="mt-2.5 flex gap-2 justify-end">
                    <button
                      onClick={() => setCreatingStatus(null)}
                      className="rounded-lg px-2.5 py-1 text-xs font-medium text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => handleAddInline(status.id)}
                      className="rounded-lg bg-blue-600 hover:bg-blue-700 px-3 py-1 text-xs font-semibold text-white shadow-xs cursor-pointer"
                    >
                      Add
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}

          {sortedStatuses.length === 0 && (
            <div className="flex flex-1 items-center justify-center">
              <p className="text-sm text-slate-400">No columns configured for this space.</p>
            </div>
          )}
        </div>
      </div>

      {/* Deep-linked Issue Modal */}
      {selectedIssue && (
        <IssueModal
          issue={selectedIssue}
          open={!!selectedIssue}
          onClose={handleCloseIssueModal}
          statuses={statuses}
          members={userOptions}
          epics={(issues as Issue[]).filter((i) => i.type === 'epic')}
          departments={(space as any)?.departments}
          onUpdate={async (id, payload) => {
            await update.mutateAsync({ id, payload })
          }}
          onDelete={async (id) => {
            await remove.mutateAsync(id)
            handleCloseIssueModal()
          }}
        />
      )}

      {/* New Issue Modal */}
      {spaceId && (
        <NewIssueModal
          open={newIssueModalOpen}
          onClose={() => setNewIssueModalOpen(false)}
          spaceId={spaceId}
        />
      )}
    </div>
  )
}
